import React, { useEffect , useState, useCallback, useRef } from 'react';
import useNavigateWithBase from "../hooks/useNavigateWithBase"; // ✅ 新しいフックをインポート
import axios from 'axios';
import AuthRoute from '../components/AuthRoute';
import UserRoleBadge from '../components/UserRoleBadge'; // ✅ コンポーネントをインポート
import StatusBadge from '../components/StatusBadge';
import '../CSS/PortalPage.css';

const PortalPage = () => {
  const navigate = useNavigateWithBase();
  const userRole = parseFloat(localStorage.getItem('role')) || 1.0;
  const userName = localStorage.getItem('loggedInUserName') || '';
  // 🔹 `useState` を正しく定義
  const [userPersonalId] = useState(localStorage.getItem('personal_id') || '');
  const [initialCompanyCode] = useState(localStorage.getItem('company_code') || '');
  const [initialCompanyName] = useState(localStorage.getItem('company_name') || '');
  
  // ✅ ログインユーザーの情報を強制リセットし、最新データを適用
  useEffect(() => {
    if (userPersonalId && userName) {
      const loggedInUser = { personal_id: userPersonalId, name: userName };
      localStorage.setItem('selectedUser', JSON.stringify(loggedInUser));
      setSelectedUser(loggedInUser);
    }
  }, [userPersonalId, userName]);

  // ✅ ログインユーザーを `selectedUser` の初期値にセット
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('selectedUser');
    if (savedUser) {
        setSelectedUser(JSON.parse(savedUser));
    } else if (userPersonalId && userName) {
        setSelectedUser({ personal_id: userPersonalId, name: userName });
    }
  }, [userPersonalId, userName]);


  const [data, setData] = useState({ companies: [], users: [], projects: [] });
  const [search, setSearch] = useState({ companies: '', users: '', projects: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1); // ✅ ページ番号のステート
  const itemsPerPage = 20; // 1ページあたりの件数
  const projectsSectionRef = useRef(null); // ✅ スクロール用リファレンス
  
  const [selectedCompany, setSelectedCompany] = useState({
    code: initialCompanyCode,
    name: initialCompanyName
  });


  useEffect(() => {
    const storedPersonalId = localStorage.getItem('loggedInPersonalId');
    const storedUserName = localStorage.getItem('loggedInUserName');

    if (storedPersonalId && storedUserName) {
        const loggedInUser = { personal_id: storedPersonalId, name: storedUserName };
        setSelectedUser(loggedInUser);
        localStorage.setItem('selectedUser', JSON.stringify(loggedInUser)); // ✅ `selectedUser` を更新
    } else {
        console.warn("⚠️ ログインユーザー情報が `localStorage` に見つかりません。");
    }
  }, []);
  
  const fetchData = useCallback(async (actionType, companyCodeFilter = null, personalIdFilter = null, pageNum = 1) => {
    setLoading(true);
    setError('');

    try {
        const token = localStorage.getItem('token');
        if (!token) {
            setError('ログイン情報が無効です。再ログインしてください。');
            setLoading(false);
            return;
        }

        const role = parseFloat(userRole.toFixed(1));

        // **適切な `company_code` をセット**
        let selectedCompanyCode = companyCodeFilter && companyCodeFilter.trim() !== "" ? 
            companyCodeFilter : selectedCompany.code;
        
        if (!selectedCompanyCode || selectedCompanyCode.includes("fetch")) {
            console.error("❌ 無効な `company_code`:", selectedCompanyCode);
            setError("会社コードが不正です。");
            setLoading(false);
            return;
        }

        // **適切な `personal_id` をセット**
        let userPersonalId = personalIdFilter && personalIdFilter.trim() !== "" ? 
            personalIdFilter : localStorage.getItem('personal_id') || "";

        // **`pageNum` のバリデーション**
        pageNum = parseInt(pageNum, 10);
        if (isNaN(pageNum) || pageNum < 1) {
            console.error("❌ 無効な `pageNum`:", pageNum);
            setError("ページ番号が無効です。");
            setLoading(false);
            return;
        }

        let requestDataBase = {
            action: actionType, // **ここで action を明示**
            role: role,
            page: pageNum,
            itemsPerPage: itemsPerPage,
            company_code: selectedCompanyCode,
            personal_id: userPersonalId
        };

        const response = await axios.post(
            'https://pmwqkcptnd.execute-api.ap-northeast-3.amazonaws.com/prod/api/list',
            JSON.stringify(requestDataBase),
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );

        if (actionType === 'fetchCompanies') {
            setData(prevData => ({ ...prevData, companies: response.data.companies || [] }));
        } else if (actionType === 'fetchUsers') {
            setData(prevData => ({ ...prevData, users: response.data.users || [] }));
        } else if (actionType === 'fetchProjects') {
            setData(prevData => ({ ...prevData, projects: response.data.projects || [] }));
            if (!response.data.projects || response.data.projects.length === 0) {
                console.warn('⚠ fetchProjects: プロジェクトデータは空です。');
            }
        }

    } catch (err) {
        setError(`データ取得に失敗しました。エラー: ${err.message}`);
    } finally {
        setLoading(false);
    }
  }, [userRole, selectedCompany.code]);

  // ✅ `selectedCompany` の変更を監視し、利用者一覧を更新
  useEffect(() => {
    if (selectedCompany.code) {
        fetchData('fetchUsers', selectedCompany.code);
        localStorage.setItem('company_name', selectedCompany.name);
    }
  }, [selectedCompany, fetchData]);

  useEffect(() => {
    // ローカルストレージから `selectedUser` を復元
    const savedUser = localStorage.getItem('selectedUser');
    if (savedUser) {
        setSelectedUser(JSON.parse(savedUser));
    }
  }, []);

  useEffect(() => {
    if (selectedCompany.code && selectedUser?.personal_id) {
        fetchData('fetchProjects', selectedCompany.code, selectedUser.personal_id);
    }
  }, [selectedCompany.code, selectedUser, fetchData]);


  // ✅ 企業を変更した際に利用者情報をリセット
  const handleCompanyClick = (newCompanyCode, newCompanyName) => {

    // ✅ 会社情報を更新
    setSelectedCompany({ code: newCompanyCode, name: newCompanyName });
    localStorage.setItem('company_code', newCompanyCode);
    localStorage.setItem('company_name', newCompanyName);

    // ✅ ユーザー情報をリセット
    setSelectedUser(null);
    localStorage.removeItem('selectedUser'); // 🔄 ローカルストレージからも削除

    // ✅ ユーザーリストとプロジェクト一覧をクリア
    setData(prevData => ({
        ...prevData,
        users: [],
        projects: []
    }));
  };
  
  const handleUserClick = (user) => {
    if (!user || !user.personal_id) {
        console.warn("⚠️ `personal_id` が不明なため、ユーザーを選択できません。");
        return;
    }

    // ✅ `selectedUser` の更新を確実に反映させるために state 更新のコールバックを使用
    setSelectedUser(prevUser => {
        const newUser = { ...user };
        localStorage.setItem('selectedUser', JSON.stringify(newUser));

        fetchData('fetchProjects', selectedCompany.code, newUser.personal_id); // ここで即座に実行

        return newUser; // `selectedUser` を更新
    });

    // ✅ プロジェクト一覧をクリア（即時反映）
    setData(prevData => ({ ...prevData, projects: [] }));
  };

  useEffect(() => {
    if (selectedUser?.personal_id) {
        fetchData('fetchProjects', selectedCompany.code, selectedUser.personal_id);
    }
  }, [selectedUser, selectedCompany.code, fetchData]); // `selectedCompany.code` を依存関係から削除
  
  const handleClearSelection = (section) => {
    setData(prevData => {
      const newData = { ...prevData };
      if (section === 'users') {
        newData.users = [];
        setSelectedCompany({ code: '', name: '' });
      } else if (section === 'projects') {
        newData.projects = [];
        setSelectedUser('');
      }
      return newData;
    });
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const handleSearch = (type, value) => {
    setSearch(prev => ({ ...prev, [type]: value }));
  };

  const filterData = (list, key) =>
    list.filter(item => Object.values(item).some(val =>
      val?.toString().toLowerCase().includes(search[key].toLowerCase())
    ));

  // 企業一覧のフィルタリング
  const filteredCompanies = filterData(data.companies, 'companies');

  const handleDeleteCompany = async (companyCode) => {
    const confirmDelete = window.confirm("削除すると紐づいた利用者、案件データもすべて削除されます。本当に削除してよろしいですか？");
    if (!confirmDelete) return;

    try {
      await axios.post('https://cbo967jtmi.execute-api.ap-northeast-3.amazonaws.com/prod/api/companies', {
        action: 'deleteCompany',
        companyCode,
      });
      alert('削除が完了しました');
      fetchData('fetchCompanies', null, null, 1);
    } catch (error) {
      console.error('削除エラー:', error);
      alert('削除に失敗しました');
    }
  };

  const handleAddCompany = () => navigate('/add-company');
  const handleAddUser = () => navigate('/add-user', { state: { companyCode: selectedCompany.code || "" } });
  const handleBulkUser = () => navigate('/bulk-user-update', { state: { companyCode: selectedCompany.code || "" } });
  const handleAddProject = () => {
    let user = selectedUser || JSON.parse(localStorage.getItem('selectedUser')) || {
        personal_id: localStorage.getItem('loggedInPersonalId'),
        name: localStorage.getItem('loggedInUserName')
    };

    if (!user || !user.personal_id) {
        alert("⚠️ 利用者を選択してください。");
        return;
    }

    navigate(`/add-project/${user.personal_id}`, { state: { selectedUserId: user.personal_id } });
  };
  const handleNavigateToSnsStrategy = (projectId, user) => {
    navigate(`/SNS-strategy-page/${projectId}`, { state: { companyName: selectedCompany.name, personalName: user.name } });
  };

  const handleReassignProjects = () => {
    const companyCode = localStorage.getItem('company_code');
    const userId = selectedUser?.personal_id || ""; // ✅ `selectedUser` を優先

    if (!companyCode || !userId) {
        alert("⚠️ 会社IDまたはユーザIDが取得できません。");
        return;
    }

    navigate('/reassign-projects', {
        state: {
            companyCode,
            userId,
        }
    });
  };

  const handleDocumentsPage = (companyName, user, projectId) => {
    if (!user) {
      alert("ユーザーを選択してください");
      return;
    }

    // ✅ クエリパラメータに情報をセットして遷移
    navigate(`/document-page`, { state: { companyName, personalName: user.name, projectId } });
  };


  // ✅ 企業一覧とユーザー一覧を取得
  useEffect(() => {
    fetchData('fetchCompanies', null, null, 1); // `company_code` と `personal_id` を適切にセット
    fetchData('fetchUsers', selectedCompany.code, null, 1);
    fetchData('fetchProjects', selectedCompany.code, userPersonalId, 1);
  }, [selectedCompany.code, userPersonalId, fetchData]);


  // `selectedCompany` の変更を監視して利用者一覧を更新
  useEffect(() => {
    if (selectedCompany) {
      fetchData('fetchUsers', selectedCompany.code, null, 1);
    }
  }, [selectedCompany, fetchData]);

  // ✅ 初回ログイン時に自分の案件一覧を取得
  useEffect(() => {
    const savedUser = localStorage.getItem('selectedUser');
    const initialUser = savedUser ? JSON.parse(savedUser) : { personal_id: userPersonalId, name: userName };

    setSelectedUser(initialUser); // ✅ ロード時に `selectedUser` を適切に設定

    if (initialUser.personal_id) {
        fetchData('fetchProjects', selectedCompany.code, initialUser.personal_id, 1);
    }

    // ✅ 案件一覧にスクロール
    setTimeout(() => {
      projectsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  }, [selectedCompany.code, userPersonalId, userName, fetchData]);

  const handleDeleteUser = async (user) => {
    if (!user || !user.personal_id) {
      alert("削除するユーザー情報が見つかりません。");
      return;
    }
  
    const confirmDelete = window.confirm(`⚠️ ${user.name} さんを削除すると、その人が作成したプロジェクトもすべて削除されます。本当に削除しますか？`);
    if (!confirmDelete) return;
  
    const requestData = {
      action: "delete",
      company_code: localStorage.getItem('company_code'),
      user: user
    };
  
    try {
      const response = await axios.post(
        "https://hmr25alvt7.execute-api.ap-northeast-3.amazonaws.com/prod/api/users",
        requestData,
        { timeout: 10000, withCredentials: false }
      );
  
      // 🚀 `response.data.message` が `undefined` の場合にエラーハンドリング
      if (response.data && response.data.message) {
        alert(response.data.message);
      } else {
        console.warn("⚠️ `response.data.message` が `undefined` です:", response.data);
        alert("削除が完了しましたが、サーバーからのメッセージが取得できませんでした。");
      }
  
      fetchData("fetchUsers", localStorage.getItem('company_code'), null, 1); // 🚀 削除後に一覧を更新
    } catch (error) {
      alert("削除に失敗しました。");
    }
  };

  const handleDeleteProject = async (projectId) => {
    const confirmDelete = window.confirm("⚠️ このプロジェクトを削除してもよろしいですか？");
    if (!confirmDelete) return;

    try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
            'https://ozq0s15jhf.execute-api.ap-northeast-3.amazonaws.com/prod/api/projects',
            JSON.stringify({ action: 'deleteProject', project_id: projectId, company_code: initialCompanyCode }),
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );

        alert(response.data.message);

        window.location.reload();

    } catch (error) {
        console.error("❌ 削除エラー:", error);
        alert("削除に失敗しました。");
    }
  };

  useEffect(() => {
    // ログインユーザーの情報を `localStorage` から取得
    const storedPersonalId = localStorage.getItem('personal_id');
    const storedUserName = localStorage.getItem('name');

    if (storedPersonalId && storedUserName) {
        const loggedInUser = { personal_id: storedPersonalId, name: storedUserName };
        setSelectedUser(loggedInUser);
        localStorage.setItem('selectedUser', JSON.stringify(loggedInUser)); // ✅ `selectedUser` を保存
    } else {
        console.warn("⚠️ ログインユーザー情報が見つかりません。");
    }
  }, []);


  // ✅ ページネーションの制御
  const handleNextPage = () => {
    setPage(prevPage => prevPage + 1);
  };

  const handlePrevPage = () => {
    setPage(prevPage => (prevPage > 1 ? prevPage - 1 : 1));
  };

  return (
    <AuthRoute>
      <div className="portal-container">
        <header className="portal-header">
          <h1 className="portal-title">ポータル画面</h1>
          <p className="welcome-message">ようこそ、<strong>{userName}</strong>さん</p>
          <button className="logout-button" onClick={handleLogout}>ログアウト</button>
        </header>

        <main className="portal-main">
          {loading && <p>データを読み込み中...</p>}
          {/*error && <p className="error-message">{error}</p>*/}

           {/* 🔹 企業一覧（管理者） */}
           {userRole >= 9.0 && (
            <div className="admin-section">
              <div className="section-header">
                <h2>登録企業一覧</h2>
                <input
                  type="text"
                  className="search-input"
                  placeholder="企業検索..."
                  value={search.companies}
                  onChange={(e) => handleSearch('companies', e.target.value)}
                />
              </div>
              <button className="add-button" onClick={handleAddCompany}>新規企業追加</button>
              {filteredCompanies.length > 0 ? (
                <>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>会社コード</th>
                        <th>会社名</th>
                        <th>責任者代表</th>
                        <th>メールアドレス</th>
                        <th>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCompanies.map(company => (
                        <tr key={company.company_code}>
                          <td>{company.company_code}</td>
                          <td><a href="#users" onClick={() => handleCompanyClick(company.company_code, company.company_name)}>{company.company_name}</a></td>
                          <td>{company.manager_name || '不明'}</td>
                          <td>{company.email || 'なし'}</td>
                          <td>
                            <button className="edit-button" onClick={() => navigate(`/edit-company/${company.company_code}`)}>編集</button>
                            <button className="delete-button" onClick={() => handleDeleteCompany(company.company_code)}>削除</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="pagination">
                    <button onClick={handlePrevPage} disabled={page === 1}>前のページ</button>
                    <span className='page'>{page}</span>
                    <button onClick={handleNextPage} disabled={filteredCompanies.length < itemsPerPage}>次のページ</button>
                  </div>
                </>
              ) : <p>企業データがありません。</p>}
            </div>
          )}

          {userRole >= 5.0 && (
            <div className="manager-section">
              <div className="section-header">
                <h2>
                    {selectedCompany ? `${selectedCompany.name}：利用者一覧` : '利用者一覧'}
                    {selectedCompany && userRole >= 9.0 && (
                      <button className="clear-button" onClick={() => handleClearSelection('users')}>クリア</button>
                    )}
                </h2>
                <input
                  type="text"
                  className="search-input"
                  placeholder="利用者検索..."
                  value={search.users}
                  onChange={(e) => setSearch(prev => ({ ...prev, users: e.target.value }))}
                />
              </div>
              <div className='add-user'>
                <button className="add-button" onClick={handleAddUser}>新規利用者個別追加</button>
                <button className="add-button" onClick={handleBulkUser}>新規利用者一括追加</button>
              </div>
              {data.users.length > 0 ? (
              <>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>個人ID</th>
                      <th>氏名</th>
                      <th>権限</th>
                      <th>メールアドレス</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.map(user => (
                      <tr key={user.personal_id}><td>{user.personal_id}</td><td><a href='#projects' onClick={() => handleUserClick(user)}>{user.name}</a></td><td><UserRoleBadge role={user.role} /></td><td>{user.email || 'なし'}</td><td><button className="edit-button" onClick={() => navigate(`/edit-user/${user.personal_id}`)}>編集</button><button className="delete-button" onClick={() => handleDeleteUser(user)}>削除</button></td></tr>
                    ))}
                  </tbody>
                </table>
                <div className="pagination">
                  <button onClick={handlePrevPage} disabled={page === 1}>前のページ</button>
                  <span className='page'>{page}</span>
                <button onClick={handleNextPage} disabled={filteredCompanies.length < itemsPerPage}>次のページ</button>
              </div>
              </>
              ) : <p>利用者データがありません。</p>}
            </div>
          )}

          {userRole >= 1.0 && (
            <section id="projects" className="project-section" ref={projectsSectionRef}>
              <div className="section-header">
                <h2>
                  {selectedUser ? `${selectedUser.name} さんの案件一覧` : '利用者が指定されていません'}
                  {selectedCompany && userRole >= 5.0 && (
                    <button className="clear-button" onClick={() => handleClearSelection('projects')}>クリア</button>
                  )}
                </h2>
                <input type="text" className="search-input" placeholder="案件検索..." value={search.projects} onChange={e => handleSearch('projects', e.target.value)} />
              </div>
              <div className=''>
                <button className="add-button" onClick={handleAddProject}>新規プロジェクト追加</button>
                  {selectedCompany && userRole >= 5.0 && (
                      <button className="add-button" onClick={handleReassignProjects}>案件付け替え</button>
                  )}  
              </div>
              {filterData(data.projects, 'projects').length > 0 ? (
              <>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>プロジェクトID</th><th>プロジェクト名</th><th>クライアント</th><th>進捗ステータス</th><th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterData(data.projects, 'projects').map(project => (
                      <tr key={project.project_id}>
                        <td>{project.project_id}</td>
                        <td>{project.project_name || project.name || '名前なし'}</td>
                        <td>{project.client_name}</td>
                        <td><StatusBadge status={project.status} /></td>
                        <td>
                          <button className="view-button" onClick={() => handleDocumentsPage(selectedCompany.name, selectedUser, project.project_id)}>資料一覧</button>
                          <button className="work-button" onClick={() => handleNavigateToSnsStrategy(project.project_id, selectedUser)}>作業する</button>
                          <button className="delete-button" onClick={() => handleDeleteProject(project.project_id)}>削除</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="pagination">
                  <button onClick={handlePrevPage} disabled={page === 1}>前のページ</button>
                  <span className='page'>{page}</span>
                  <button onClick={handleNextPage} disabled={filteredCompanies.length < itemsPerPage}>次のページ</button>
                </div>
              </>  
              ) : <p>案件データがありません。</p>}
            </section>
          )}
        </main>
      </div>
    </AuthRoute>
  );
};

export default PortalPage;
